import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import { useShop } from '../context/Shop'
import { rubriqueParams } from '../context/Shop'
import { api } from '../lib/api'
import { eur, imageUrl, plain, ttc } from '../lib/format'
import type { ProductPage } from '../lib/types'

// Recherche avec rubrique + suggestions (rubriques du menu et produits)
export default function SearchBox({ id }: { id: string }) {
  const { menu } = useShop()
  const navigate = useNavigate()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const [q, setQ] = useState(params.get('q') ?? '')
  const [rubrique, setRubrique] = useState('')
  const [open, setOpen] = useState(false)
  const [products, setProducts] = useState<ProductPage['items']>([])
  const wrap = useRef<HTMLDivElement>(null)

  useEffect(() => { setQ(new URLSearchParams(location.search).get('q') ?? '') }, [location.search])
  useEffect(() => {
    if (q.trim().length < 2) return setProducts([])
    const t = setTimeout(() => api<ProductPage>(`/produits?per_page=4&q=${encodeURIComponent(q)}`).then((d) => setProducts(d.items)).catch(() => null), 200)
    return () => clearTimeout(t)
  }, [q])
  useEffect(() => {
    const close = (e: MouseEvent) => !wrap.current?.contains(e.target as Node) && setOpen(false)
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [])

  const words = plain(q).split(/\s+/).filter((w) => w.length > 1)
  const links = words.length
    ? menu.flatMap((e) => (e.items ? e.items.map((l) => ({ ...l, context: e.label })) : e.columns!.flatMap((c) => c.items.map((l) => ({ ...l, context: c.title })))))
        .filter((l) => words.every((w) => plain(`${l.label} ${l.context}`).includes(w.replace(/s$/, ''))))
        .slice(0, 6)
    : []
  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setOpen(false)
    const extra = rubriqueParams(menu, rubrique)
    navigate(`/catalogue?${[q.trim() && `q=${encodeURIComponent(q.trim())}`, extra].filter(Boolean).join('&')}`)
  }
  const go = (to: string) => {
    setOpen(false)
    navigate(to)
  }
  const sportsCo = menu.find((m) => m.id === 'sports-co')?.columns ?? []

  return (
    <div className="relative w-full" ref={wrap}>
      <form onSubmit={submit} role="search" className="relative flex w-full bg-slate-800/80 rounded-lg border border-slate-700/80 focus-within:border-brand-orange transition-colors overflow-hidden">
        <select value={rubrique} onChange={(e) => setRubrique(e.target.value)} aria-label="Catégorie" className="bg-slate-900/90 text-xs text-slate-300 px-3 border-r border-slate-700 focus:outline-none cursor-pointer max-w-[34%] sm:max-w-[42%]" data-testid={`${id}-rubrique`}>
          <option value="">Toutes Catégories</option>
          <optgroup label="Sports co">{sportsCo.map((c) => <option key={c.accent} value={c.accent}>{c.title}</option>)}</optgroup>
          {menu.filter((m) => m.id !== 'sports-co').map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        <input type="search" value={q} onChange={(e) => { setQ(e.target.value); setOpen(true) }} onFocus={() => setOpen(true)} autoComplete="off" placeholder="Chercher un ballon, maillot, résine, pétanque..." className="w-full min-w-0 bg-transparent px-4 py-2.5 text-sm text-white placeholder-slate-400 focus:outline-none border-0 focus:ring-0" data-testid={`${id}-input`} />
        <button type="submit" className="bg-brand-ink hover:bg-orange-800 px-4 text-white transition-colors" aria-label="Lancer la recherche" data-testid={`${id}-submit`}><Search className="w-4 h-4" /></button>
      </form>
      {open && q.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-white text-slate-800 rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden" data-testid={`${id}-suggestions`}>
          {links.length > 0 && <div className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Rubriques</div>}
          {links.map((l) => (
            <button key={l.to} type="button" onClick={() => go(l.to)} className="w-full flex items-center justify-between gap-3 px-4 py-2 text-sm hover:bg-slate-50 text-left">
              <span className="text-navy-900 font-medium">{l.label}</span><span className="text-[11px] text-slate-400">{l.context}</span>
            </button>
          ))}
          {products.length > 0 && <div className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Produits</div>}
          {products.map((p) => (
            <button key={p.id} type="button" onClick={() => go(`/produit/${p.id}`)} className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 text-left">
              <img src={imageUrl(p.images[0], 80)} alt="" className="w-9 h-9 rounded-md object-cover bg-slate-100 shrink-0" />
              <span className="flex-1 min-w-0 text-sm text-navy-900 truncate">{p.title}</span>
              <span className={`text-xs font-bold ${p.price.rate ? 'text-red-600' : 'text-navy-900'}`}>{p.price.varies ? 'dès ' : ''}{eur(ttc(p.price.final_ht))} TTC</span>
            </button>
          ))}
          {!links.length && !products.length && <p className="px-4 py-3 text-sm text-slate-500">Aucun résultat pour « {q} ». <Link to="/devis" className="font-semibold text-brand-orange hover:underline">Demandez-nous un devis</Link></p>}
          <button type="button" onClick={submit} className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-bold text-brand-orange hover:bg-orange-50 border-t border-slate-100"><Search className="w-3.5 h-3.5" /> Voir tous les résultats pour « {q} »</button>
        </div>
      )}
    </div>
  )
}
